// File: carWizardTypes
// Created: 2026-05-12
// Updated: 2026-05-29
// Author: Erick Hernández Silva

export interface ViewImages {
  view1?: string;
  view2?: string;
  view3?: string;
  view4?: string;
  view5?: string;
  view6?: string;
}

export const VIEW_KEYS = ['view1', 'view2', 'view3', 'view4', 'view5', 'view6'] as const;
export type ViewKey = (typeof VIEW_KEYS)[number];
export type PreferredView = ViewKey | 'no_change';

export interface SubOption {
  optionId: string;
  optionName: string;
  optionPrice: number;
  optionDescription?: string;
  images: ViewImages;
  isDefault?: boolean;
  // New dynamic model: this sub-option is active when the linked step's
  // selected option equals linkedOptionId. See Option.subOptionsLinkedToStepId.
  linkedOptionId?: string;
}

export interface Option {
  optionId: string;
  optionName: string;
  optionDescription?: string;
  optionPrice: number;
  presentationImage?: string;
  images: ViewImages;
  subOptions?: SubOption[];
  isDefault?: boolean;
  includedInBaseRender?: boolean;
  // New dynamic model: when set, this option's sub-options are coupled to the
  // selection of another step (by stepId). Sub-options resolve via linkedOptionId.
  subOptionsLinkedToStepId?: string;
  // Legacy (pre-dynamic-model) coupling by name. Kept optional for back-compat.
  contrastSeatingName?: string;
  contrastSteeringName?: string;
}

export interface Step {
  // New dynamic model: stable slug id, used by screens + rules + state keying.
  stepId: string;
  stepName: string;
  stepDescription: string;
  isTextOnly?: boolean;
  preferredView?: PreferredView;
  options: Option[];
  multiSelect?: boolean;
  required?: boolean;
  // Legacy ordinal. Only present on un-migrated cars; do not rely on it.
  stepNumber?: number;
}

export type Layout = 'stack' | 'tabs' | 'grid';

export interface Screen {
  screenId: string;
  screenName: string;
  screenDescription?: string;
  order: number;
  stepIds: string[];
  layout?: Layout;
}

export type RuleEffect =
  | { type: 'disableOption'; targetStepId: string; targetOptionId: string }
  | { type: 'hideOption'; targetStepId: string; targetOptionId: string }
  | { type: 'disableStep'; targetStepId: string }
  | { type: 'hideStep'; targetStepId: string }
  | { type: 'hideScreen'; targetScreenId: string }
  | { type: 'forceOption'; targetStepId: string; targetOptionId: string }
  // Locks the variant of a sub-option-bearing option while it is selected
  // (e.g. Contrast package → Original steering wheel always Onyx/black).
  | { type: 'forceSubOption'; targetStepId: string; targetOptionId: string; targetSubOptionId: string };

export type RuleEffectType = RuleEffect['type'];

export interface RuleCondition {
  // AND between items.
  selections: Array<{ stepId: string; optionIdIn: string[] }>;
}

export interface Rule {
  ruleId: string;
  when: RuleCondition;
  then: RuleEffect[];
}

export interface CarConfig {
  id: string;
  createdAt: string;
  updatedAt?: string;
  carName: string;
  basePrice: number;
  baseRenderOptionId?: string;
  steps: Step[];
  screens: Screen[];
  rules?: Rule[];
  // Model picker (splash). The card label is carName.
  isPublished?: boolean;
  displayOrder?: number;
  splash?: { image: string; description?: string };
  overlays?: Overlay[];
  sceneries?: Scenery[];
  // Model manager: cars of the same family share modelId (slug) / modelName.
  modelId?: string;
  modelName?: string;
  // Staging only: when this car was last published to production.
  lastPublishedAt?: string;
}

export type CarVersionSource = 'save' | 'create' | 'publish' | 'before-publish';

export interface CarVersionMeta {
  carKey: string;
  versionAt: string;
  source: CarVersionSource;
  note?: string;
  carName: string;
  modelName?: string;
  stage: string;
  bytes: number;
}

export type OverlayPosition = 'top' | 'bottom' | 'afterStep';

/** Toggleable render layer (e.g. "show frame", "show soft top"). Never priced. */
export interface Overlay {
  overlayId: string;
  label: string;
  labelActive?: string;
  images: ViewImages;
  // Where the layer sits in the stack: above everything, just above the base
  // render, or right above a given step's layer.
  placement: { position: OverlayPosition; stepId?: string };
  // Step layers hidden while this overlay is on.
  hideStepIds?: string[];
  // Overlays sharing a group are mutually exclusive.
  exclusiveGroup?: string;
  defaultOn?: boolean;
  order?: number;
}

/** Background behind the car (car renders carry alpha). */
export interface Scenery {
  sceneryId: string;
  name: string;
  thumbnail?: string;
  images: ViewImages;
  shadowImages?: ViewImages;
  isDefault?: boolean;
}

export interface ShowroomItem {
  id: string;
  createdAt: string;
  carName: string;
  modelName?: string;
  displayOrder?: number;
  splash?: { image: string; description?: string };
}
